import React, { useContext, useEffect, useRef, useState } from "react";
import Board, { moveCard } from "@asseinfo/react-kanban";
import "@asseinfo/react-kanban/dist/styles.css";
import SalesFunnelHeader from "./SalesFunnelHeader";
import api from "../../services/api";
import Card from "./Card";
import toastError from "../../errors/toastError";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import "../../css/pages/SalesFunnel/kanban.css";
import "../../css/pages/SalesFunnel/SalesFunnel.css";
import { TicketModal } from "../../components/TicketModal/ticketModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const TAILWIND_CARD_COLORS = [
	"#6b7280", "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
	"#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9", "#3b82f6",
	"#6366f1", "#8b5cf6", "#a855f7", "#d946ef", "#ec4899", "#f43f5e",
];

function useClickAway(ref, onClickAway) {
	useEffect(() => {
		function handleClick(e) {
			if (ref.current && !ref.current.contains(e.target)) onClickAway();
		}
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [ref, onClickAway]);
}

const SalesFunnel = () => {
	const { darkMode } = useContext(ThemeContext);
	const [searchParam, setSearchParam] = useState("");
	const [loading, setLoading] = useState(false);
	const [options, setOptions] = useState([]);
	const [board, setBoard] = useState({ columns: [] });
	const [inputOpen, setInputOpen] = useState({ open: false, columnId: null });
	const [newColumnOpen, setNewColumnOpen] = useState(false);
	const [editingColumn, setEditingColumn] = useState({ id: null, title: null });
	const [users, setUsers] = useState([]);
	const [valores, setValores] = useState({ totalValor: 0, totalContacts: 0 });
	const [newColumnName, setNewColumnName] = useState("");
	const [columnMenuOpen, setColumnMenuOpen] = useState(null);
	const editRef = useRef(null);
	const addColumnRef = useRef(null);
	const autocompleteRef = useRef(null);
	const columnMenuRef = useRef(null);
	const boardWrapRef = useRef(null);
	const [conversationOpen, setConversationOpen] = useState(false);
	const [ticketId, setTicketId] = useState(null);
	const [FunnelId, setFunnelId] = useState(parseInt(window.location.hash.replace('#','')));

	const { user } = useContext(AuthContext);
	const userId = user?.profile !== "admin" ? user?.id : null;

	useEffect(() => {
		const boardEl = boardWrapRef.current?.querySelector(".react-kanban-board");
		if (boardEl) {
			boardEl.style.minHeight = "310px";
		}
	}, []);

	useClickAway(editRef, () => setEditingColumn({ id: null, title: null }));
	useClickAway(addColumnRef, () => setNewColumnOpen(false));
	useClickAway(autocompleteRef, () => setInputOpen({ open: false, columnId: null }));
	useClickAway(columnMenuRef, () => setColumnMenuOpen(null));

	useEffect(() => {
		(async () => {
			try {
				const { data: { users: u } } = await api.get("/users/");
				setUsers(u);
			} catch (error) {
				toastError(error);
			}
		})();
	}, []);

	const reloadBoard = async () => {
		if (!FunnelId){
			setBoard({ columns: [] });
			return
		}
		try {
			const { data } = await api.get("/sales-funnel/column",
				{
					params: {
						workspace: "contacts",
						salesFunnelId: FunnelId,
						requestUserId: userId
					}
				});
			setBoard({
				columns: data.map((col) => ({
					id: col.id,
					title: col.name,
					color: col.color,
					cardColor: col.cardColor ?? null,
					cards: (col.contacts || []).map((c) => ({ ...c, columnCardColor: col.cardColor ?? null, salesFunnelColumnId:col.id  }))
				})),
			});
		} catch (error) {
			toastError(error);
			setFunnelId(null);
			setBoard({ columns: [] });
		}
	};

	const openConversation = (ticketId) => {
		setTicketId(ticketId);
		setConversationOpen(true);
	};

	const closeConversation = () => {
		setConversationOpen(false);
		setTicketId(null);
	};

	useEffect(() => {
		if(!FunnelId){
			setBoard({ columns: [] });
			return;
		}
		(async () => {
			try {
				const { data } = await api.get("/sales-funnel/column", {
					params: {
						workspace: "contacts",
						salesFunnelId: FunnelId,
						requestUserId: userId
					} });
				setBoard({
					columns: data.map((col) => ({
						id: col.id,
						title: col.name,
						color: col.color,
						cardColor: col.cardColor ?? null,
						cards: (col.contacts || []).map((c) => ({ ...c, columnCardColor: col.cardColor ?? null, salesFunnelColumnId:col.id })),
						valor: col.valor,
					})),
				});
				setValores({
					totalValor: data.reduce(
						(acc, col) =>
							acc +
							col.contacts.reduce((total, contact) => {
								const fc = contact.funnelContact ?? contact.FunnelContact;
								return total + (parseFloat(fc?.valor) || 0);
							}, 0),
						0
					),
					totalContacts: data.reduce((acc, col) => acc + col.contacts.length, 0),
				});
			} catch (error) {
				toastError(error);
				window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
				setFunnelId(null);
				setBoard({ columns: [] });
				setValores({ totalValor: 0, totalContacts: 0 });
			}
		})();
	}, [FunnelId, userId]);

	useEffect(() => {
		if (!board.columns.length) return;
		const totalValor = board.columns.reduce(
			(acc, col) =>
				acc +
				col.cards.reduce((sum, card) => {
					const fc = card.funnelContact ?? card.FunnelContact;
					return sum + (parseFloat(fc?.valor) || 0);
				}, 0),
			0
		);
		const totalContacts = board.columns.reduce((acc, col) => acc + col.cards.length, 0);
		setValores({ totalValor, totalContacts });
	}, [board]);

	const handleCardMove = async (_card, source, destination) => {
		const oldBoard = board;
		const newBoard = moveCard(board, source, destination);
		const normalizedBoard = {
			columns: newBoard.columns.map((col) => ({
				...col,
				cards: col.cards.map((card) => ({ ...card, columnCardColor: col.cardColor ?? null })),
			})),
		};
		try {
			setBoard(normalizedBoard);
			await api.post("/sales-funnel/pin-contact", {
			contactId: _card.id,
			salesFunnelColumnId: destination.toColumnId,
			requestUserId: userId
		});
		} catch (error) {
			toastError(error);

			setBoard(oldBoard);
		}

	};

	const removeCard = async (contactId) => {
		setBoard({
			columns: board.columns.map((col) => ({
				...col,
				cards: col.cards.filter((card) => card.id !== contactId),
			})),
		});
	};

	const addColumn = (e) => {
		const name = e?.target?.value ?? newColumnName;
		if (!name) return;
		try {
			api.post("/sales-funnel/column", { name, workspace: "contacts", salesFunnelId: FunnelId, requestUserId: userId }).then(({ data }) => {
				setBoard({
					columns: [
						...board.columns,
						{ id: data.id, title: data.name, color: null, cardColor: null, cards: [] },
					],
				});
				setNewColumnOpen(false);
				setNewColumnName("");
			});
		} catch (error) {
			toastError(error);
			setBoard({ columns: [] });
		}
	};

	const removeColumn = async (columnId) => {
		try {
			await api.delete(`/sales-funnel/column/${columnId}`, {
				data: {
					salesFunnelId: FunnelId,
					requestUserId: userId
				}
			});
		} catch (error) {
			toastError(error);
			return;
		}
		reloadBoard();
	};

	const updateColumn = async ({ columnId, name, color, cardColor }) => {
		try {
			if (!FunnelId) return
			await api.put(`/sales-funnel/column/${columnId}`, { name, color, cardColor, salesFunnelId:FunnelId, requestUserId: userId });
			reloadBoard();
		} catch (error) {
			toastError(error);
		}
	};

	useEffect(() => {
		if (searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const t = setTimeout(() => {
			api.get("contacts", { params: { searchParam } })
				.then(({ data }) => setOptions(data.contacts || []))
				.catch(toastError)
				.finally(() => setLoading(false));
		}, 500);
		return () => clearTimeout(t);
	}, [searchParam]);

	const getFilteredOptions = () => {
		const term = searchParam.trim().toLowerCase();
		if (!term) return options;
		return options.filter(
			(opt) =>
				(opt.name && opt.name.toLowerCase().includes(term)) ||
				(opt.number && String(opt.number).includes(term))
		);
	};

	const handleSelectOption = async (option) => {
		if (!option || !option.id) return;
		try {
			await api.post("/sales-funnel/pin-contact", {
				contactId: option.id,
				salesFunnelColumnId: inputOpen.columnId,
				requestUserId: userId
			});
			reloadBoard();
			setInputOpen({ open: false, columnId: null });
		} catch (error) {
			toastError(error);
		}
	};

	const handleColumnPositionChange = async (column, source, destination) => {
		if (!FunnelId) return
		try {
			await api.put(`/sales-funnel/column/${column.id}`, { position: destination.toPosition, salesFunnelId: FunnelId, requestUserId: userId });
		} catch (error) {
			toastError(error);
		}
		reloadBoard();
	};

		const renderColumnHeader = ({ title, id, color }) => {
		const col = board.columns.find((c) => c.title === title);
		const cardColor = col?.cardColor ?? null;
		const cardCount = col?.cards?.length ?? 0;
		const columnValor = col?.cards?.reduce(
			(acc, card) => acc + (parseFloat(card.FunnelContact?.valor) || 0),
			0
		) ?? 0;

		const headerColor = (cardColor || color) || "#1f1f1f";

		return (
			<div
				className="sf-column-header-card"
				style={{
					background: "transparent",
					padding: "1rem 1rem 0.5rem",
					marginBottom: "0.25rem",
				}}
			>
				<div className="sf-column-header">
					<div className="sf-column-header-top">
						<div className="sf-column-badges">
							<span className="sf-column-badge sf-column-badge-leads">
								{cardCount} {cardCount !== 1 ? "Leads" : "Lead"}
							</span>
							<span className="sf-column-badge sf-column-badge-value">
								R$ {Number(String(columnValor).replace(",", ".")).toFixed(2)}
							</span>
						</div>
						<div
							className="sf-column-menu-wrap"
							ref={columnMenuOpen === id ? columnMenuRef : null}
						>
						<button
							type="button"
							className="sf-column-menu-btn"
							onClick={() => setColumnMenuOpen(columnMenuOpen === id ? null : id)}
							title="Opções da coluna"
							aria-label="Opções da coluna"
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
								<path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
							</svg>
						</button>
						{columnMenuOpen === id && (
							<div className="sf-column-menu">
								<div className="sf-column-menu-card-color">
									<span className="sf-column-menu-card-color-label">Cor da borda do cartão</span>
									<div className="sf-column-menu-swatches">
										{TAILWIND_CARD_COLORS.map((hex) => (
											<button
												key={hex}
												type="button"
												className="sf-column-menu-swatch"
												style={{ background: hex }}
												title={hex}
												aria-label={`Cor ${hex}`}
												onClick={() => {
													updateColumn({ columnId: id, cardColor: hex });
													setColumnMenuOpen(null);
												}}
											>
												{cardColor === hex && (
													<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 12l5 5L20 7"/></svg>
												)}
											</button>
										))}
										<button
											type="button"
											className="sf-column-menu-swatch sf-column-menu-swatch-none"
											title="Sem cor"
											aria-label="Remover cor da borda"
											onClick={() => {
												updateColumn({ columnId: id, cardColor: null });
												setColumnMenuOpen(null);
											}}
										>
											{!cardColor && "✕"}
										</button>
									</div>
								</div>
								<button
									type="button"
									className="sf-column-menu-item sf-column-menu-item-danger"
									onClick={() => {
										removeColumn(id);
										setColumnMenuOpen(null);
									}}
								>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
									Remover coluna
								</button>
							</div>
						)}
						</div>
					</div>
					<div className="sf-column-title" ref={editRef}>
						{editingColumn.id === id ? (
							<input
								className="sf-column-input"
								autoFocus
								value={editingColumn.title}
								onChange={(e) => setEditingColumn({ id, title: e.target.value })}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										updateColumn({ columnId: id, name: editingColumn.title });
										setEditingColumn({ id: null, title: null });
									}
								}}
								placeholder="Nome da coluna"
							/>
						) : (
							<span
								className="titleColumnsKanban"
								style={{ color: (cardColor || color) || "#1f1f1f", textTransform: "uppercase", cursor: "pointer" }}
								onClick={() => setEditingColumn({ id, title })}
							>
								{title}
							</span>
						)}
					</div>
				</div>
				<div
					style={{
						width: "100%",
						height: 4,
						borderRadius: 20,
						background: headerColor,
						marginBottom: 15,
					}}
				/>
				{inputOpen.open && inputOpen.columnId === id ? (
					<div ref={autocompleteRef} className="sf-autocomplete-wrap">
						<span className="sf-autocomplete-icon" aria-hidden>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
						</span>
						<input
							className="sf-autocomplete-input"
							autoFocus
							placeholder="Buscar contato..."
							value={searchParam}
							onChange={(e) => setSearchParam(e.target.value)}
							onKeyDown={(e) => e.key === "Escape" && setInputOpen({ open: false, columnId: null })}
						/>
						{loading && <span className="sf-autocomplete-spinner" />}
						{searchParam.length >= 3 && (
							<ul className="sf-autocomplete-list">
								{getFilteredOptions().slice(0, 10).map((opt) => (
									<li
										key={opt.id}
										className="sf-autocomplete-item"
										onClick={() => handleSelectOption(opt)}
									>
										{opt.number ? `${opt.name} - ${opt.number}` : opt.name}
									</li>
								))}
								{getFilteredOptions().length === 0 && !loading && (
									<li className="sf-autocomplete-item sf-autocomplete-item-empty">
										Nenhum contato encontrado
									</li>
								)}
							</ul>
						)}
					</div>
				) : (
					<button
						type="button"
						className="sf-add-card-btn"
						onClick={() =>
							setInputOpen({
								open: true,
								columnId: board.columns.find((c) => c.title === title)?.id,
							})
						}
					>
						<svg className="sf-add-card-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
						Adicionar cartão
					</button>
				)}
			</div>
		);
	};

	useEffect(() => {
		const onHashChange = () => {
			setFunnelId(parseInt(window.location.hash.replace('#','')));
		};
		window.addEventListener("hashchange", onHashChange);

		return () => {
			window.removeEventListener("hashchange", onHashChange);
		};
	}, []);

	return (
		<div className="sf-funnel-page">
			<SalesFunnelHeader
				valores={valores}
				onAddColumn={() => {
					if (FunnelId) {
						setNewColumnOpen(true)
					} else {
						toastError("Selecione um funil de vendas")
					};
				}} />
			<div ref={boardWrapRef} className={darkMode ? "sf-board-wrap sf-board-wrap--dark" : "sf-board-wrap"}>
				<Board
					allowAddColumn
				onCardDragEnd={handleCardMove}
				onColumnDragEnd={handleColumnPositionChange}
				renderCard={(card) => (
					<React.Fragment key={card.id ?? card.contactId ?? `card-${String(card.name)}`}>
						<Card
							contact={card}
							removeCard={removeCard}
							users={users}
							reloadBoard={reloadBoard}
							openConversation={openConversation}

						/>
					</React.Fragment>
				)}
				renderColumnHeader={renderColumnHeader}
				renderColumnAdder={() => {
					if(FunnelId){
						if (newColumnOpen) {
						return (
							<div ref={addColumnRef}>
								<input
									className="sf-column-input"
									autoFocus
									placeholder="Nome da coluna"
									value={newColumnName}
									onChange={(e) => setNewColumnName(e.target.value)}
									onKeyPress={(e) => {
										if (e.key === "Enter") addColumn();
									}}
								/>
							</div>
						);
					}
					return (
						<button
							type="button"
							className="sf-add-column-btn"
							onClick={() => setNewColumnOpen(true)}
						>
							Adicionar coluna
						</button>
					)
					} else {
						return (<div style={{width: "100%",display: "flex",justifyContent: "center",alignItems: "center"}}>
								<p>Selecione um funil de vendas</p>
							</div>)
					}

				}}
			>
				{board}
			</Board>

				{conversationOpen && ticketId && (
					<TicketModal
						ticketId={ticketId}
						onClose={closeConversation}
					/>
				)}
			</div>
		</div>
	);

};

export default SalesFunnel;
